import React, { ReactElement, use, useEffect, useState } from 'react';
import { Options, SankeyNodeObject, TooltipOptions, Point } from 'highcharts';
import Highcharts from 'highcharts/esm/highcharts.js';
import 'highcharts/esm/modules/sankey.js';
import { HighchartsReact } from 'highcharts-react-official';
import ReactDOMServer from 'react-dom/server';
import styles from '@components-clean/core/charts/shared/style.module.scss';
import { CHART_HEIGHT_PIXELS } from '@components-clean/core/charts/shared/utils';
import Loading from '@components-clean/core/loading/loading';
import { PopOutPage } from '@components-clean/core/page-toolbar/page-toolbar';
import { HC_RAG_NAMES } from '@interfaces/opra-chart.interface';
import { SankeyChartData } from '@interfaces-clean/panels.interface';
import { Permissions } from 'contexts/user-context';
import { UserContext } from '@contexts/user-context';
import { useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { ShowMessage } from '@utils/notification';
import { AllPaths } from '@utils/router/all-paths';

export interface HighchartsTooltipElementProps {
    context: Point | SankeyNodeObject;
}

export interface IPopoutInfo {
    dataCutDate?: string | undefined;
    comparisonDataCutDate?: string | undefined;
    dataviewRef?: string | null;
    showPopoutIcon?: boolean;
}

interface SankeyChartProps {
    chartRef?: React.RefObject<HighchartsReact.RefObject | null>;
    panelData: SankeyChartData;
    TooltipElement?: (props: HighchartsTooltipElementProps) => ReactElement;
    height?: number;
    popoutInfo?: IPopoutInfo;
    exportFilename?: string;
}

export enum SankeyKeys {
    RED = 'Red',
    AMBER = 'Amber',
    GREEN = 'Green',
    PURPLE = 'Purple',
    GRAY = 'Gray',
}

interface ExtendedChart extends Highcharts.Chart {
    labels: Highcharts.SVGElement[];
}

export const SankeyChart = ({
    chartRef,
    panelData,
    TooltipElement,
    height,
    popoutInfo,
    exportFilename,
}: SankeyChartProps) => {
    const { title, data } = panelData;
    const [options, setOptions] = useState<Options>({});
    const { user } = use(UserContext);
    const { t } = useTranslation('cm');
    const columnLabels = [popoutInfo?.comparisonDataCutDate, '', popoutInfo?.dataCutDate];

    const popoutDataview = (ragColor: number | undefined, datasetCutDate: string, dataviewRef: string) => {
        if (
            !user?.can?.({
                module: Constants.Modules.CM2,
                page: Constants.PageElements.DataViewsCM2,
                permission: Permissions.Enabled,
                element: Constants.PageElements.DataViewsCM2,
            })
        )
            return;

        if (ragColor !== undefined) {
            const dataViewRoute = AllPaths.path('cm2-data-views', {
                dataView: dataviewRef,
            });
            PopOutPage(
                user?.organisationKey,
                `${dataViewRoute}?sitescore=${ragColor}&datasetcutdate=${datasetCutDate}`,
            );
        }
    };

    useEffect(() => {
        let tooltip: TooltipOptions = {
            shared: false,
            pointFormat: '{point.fromNode.name} \u2192 {point.toNode.name}: {point.weight}',
        };

        if (TooltipElement) {
            tooltip = {
                ...tooltip,
                useHTML: true,
                formatter(this: Point | SankeyNodeObject) {
                    return ReactDOMServer.renderToString(<TooltipElement context={this} />);
                },
            };
        }

        const FROM = {
            GREEN: 'from green',
            AMBER: 'from amber',
            RED: 'from red',
            PURPLE: 'from purple',
            GRAY: 'from gray',
        };
        const TO = {
            GREEN: 'to green',
            AMBER: 'to amber',
            RED: 'to red',
            PURPLE: 'to purple',
            GRAY: 'to gray',
        };

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setOptions({
            exporting: {
                chartOptions: {
                    title: { text: panelData?.title, style: { fontWeight: 'semi-bold' } },
                    chart: {
                        events: {
                            load() {
                                const chart: Highcharts.Chart = this;

                                const positions = [10, chart.chartWidth - 80];
                                chart.renderer.text(columnLabels[0], positions[0], chart.chartHeight - 10).add();
                                chart.renderer.text(columnLabels[2], positions[1], chart.chartHeight - 10).add();
                            },
                        },
                    },
                },
                filename: exportFilename,
            },
            chart: {
                height: height ?? CHART_HEIGHT_PIXELS,
                events: {
                    render: () => {
                        const chart: ExtendedChart = chartRef?.current?.chart;

                        if (chart.labels && chart.labels.length > 0) {
                            chart.labels.forEach((label) => {
                                label.destroy();
                            });
                        }
                        const positions = [40, chart.chartWidth / 2, chart.chartWidth - 40];
                        chart.labels = [];
                        columnLabels.forEach((label, i) => {
                            chart.labels.push(
                                chart.renderer
                                    .text(columnLabels[i], positions[i], chart.chartHeight - 10)
                                    .attr({
                                        align: 'center',
                                    })
                                    .css({
                                        fontSize: '12px',
                                    })
                                    .attr({
                                        zIndex: 5,
                                    })
                                    .add(),
                            );
                        });
                    },
                },
            },
            accessibility: {
                point: {
                    valueDescriptionFormat: '{index}. {point.from} to {point.to}, {point.weight}.',
                },
            },
            tooltip,
            plotOptions: {
                series: {
                    point: {
                        events: {
                            click(eve) {
                                const popoutData = eve.point.series.options?.custom?.popout_info as IPopoutInfo;
                                if (popoutData) {
                                    if (popoutData.dataviewRef) {
                                        if (eve.point.options.id) {
                                            const ragColorIndex = HC_RAG_NAMES.indexOf(eve.point.name);
                                            if (ragColorIndex !== -1) {
                                                let dataSetDate = popoutData.dataCutDate;

                                                if (eve.point.options.id.startsWith('from '))
                                                    dataSetDate = popoutData.comparisonDataCutDate;

                                                popoutDataview(
                                                    ragColorIndex,
                                                    dataSetDate as string,
                                                    popoutData.dataviewRef,
                                                );
                                            }
                                        }
                                    } else if (eve.point.options.id) {
                                        ShowMessage({
                                            text: t('componentsClean.panels.noDataviewConfigured'),
                                            type: 'none',
                                        });
                                    }
                                }
                            },
                        },
                    },
                },
            },
            xAxis: {
                visible: false,
            },
            yAxis: {
                visible: false,
            },
            series: [
                {
                    type: 'sankey',
                    name: title,
                    keys: ['from', 'to', 'weight'],
                    custom: {
                        popout_info: popoutInfo,
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    nodes: [
                        {
                            id: FROM.RED,
                            name: SankeyKeys.RED,
                            color: styles['rag-colour-red'],
                        },
                        {
                            id: FROM.AMBER,
                            name: SankeyKeys.AMBER,
                            color: styles['rag-colour-amber'],
                        },
                        {
                            id: FROM.GREEN,
                            name: SankeyKeys.GREEN,
                            color: styles['rag-colour-green'],
                        },
                        {
                            id: FROM.GRAY,
                            name: SankeyKeys.GRAY,
                            color: '#cccccc',
                        },
                        {
                            id: FROM.PURPLE,
                            name: SankeyKeys.PURPLE,
                            color: styles['rag-colour-purple'],
                        },
                        {
                            id: TO.RED,
                            name: SankeyKeys.RED,
                            color: styles['rag-colour-red'],
                        },
                        {
                            id: TO.AMBER,
                            name: SankeyKeys.AMBER,
                            color: styles['rag-colour-amber'],
                        },
                        {
                            id: TO.GREEN,
                            name: SankeyKeys.GREEN,
                            color: styles['rag-colour-green'],
                        },
                        {
                            id: TO.GRAY,
                            name: SankeyKeys.GRAY,
                            color: '#cccccc',
                        },
                        {
                            id: TO.PURPLE,
                            name: SankeyKeys.PURPLE,
                            color: styles['rag-colour-purple'],
                        },
                    ],
                    data: [
                        // Add mappings from gray
                        [FROM.GRAY, TO.GRAY, data.gray.toGray],
                        [FROM.GRAY, TO.RED, data.gray.toRed],
                        [FROM.GRAY, TO.AMBER, data.gray.toAmber],
                        [FROM.GRAY, TO.GREEN, data.gray.toGreen],
                        ...(data.red.toPurple ? [[FROM.GRAY, TO.PURPLE, data.gray.toPurple!]] : []),

                        // Add mappings from red
                        [FROM.RED, TO.GRAY, data.red.toGray],
                        [FROM.RED, TO.RED, data.red.toRed],
                        [FROM.RED, TO.AMBER, data.red.toAmber],
                        [FROM.RED, TO.GREEN, data.red.toGreen],
                        ...(data.red.toPurple ? [[FROM.RED, TO.PURPLE, data.red.toPurple]] : []),

                        // Add mappings from amber
                        [FROM.AMBER, TO.GRAY, data.amber.toGray],
                        [FROM.AMBER, TO.RED, data.amber.toRed],
                        [FROM.AMBER, TO.AMBER, data.amber.toAmber],
                        [FROM.AMBER, TO.GREEN, data.amber.toGreen],
                        ...(data.amber.toPurple ? [[FROM.AMBER, TO.PURPLE, data.amber.toPurple]] : []),

                        // Add mappings from green
                        [FROM.GREEN, TO.GRAY, data.green.toGray],
                        [FROM.GREEN, TO.RED, data.green.toRed],
                        [FROM.GREEN, TO.AMBER, data.green.toAmber],
                        [FROM.GREEN, TO.GREEN, data.green.toGreen],
                        ...(data.green.toPurple ? [[FROM.GREEN, TO.PURPLE, data.green.toPurple]] : []),

                        // (Maybe) add mappings from purple
                        ...(data.purple
                            ? [
                                  [FROM.PURPLE, TO.GRAY, data.purple.toGray],
                                  [FROM.PURPLE, TO.RED, data.purple.toRed],
                                  [FROM.PURPLE, TO.AMBER, data.purple.toAmber],
                                  [FROM.PURPLE, TO.GREEN, data.purple.toGreen],
                                  [FROM.PURPLE, TO.PURPLE, data.purple.toPurple!],
                              ]
                            : []),
                    ],
                },
            ],
        });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [panelData, TooltipElement]);

    if (options === null) return <Loading />;

    return <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="chart" options={options} />;
};
